import React, { useEffect, useMemo, useState } from 'react';
import { connect, useDispatch } from 'react-redux';

// Actions
import { showModal as showOmniRiskProfileModal, setIsRenamingQuestionnaire } from '@/redux/modules/omni-risk-profile-modal';
import { loadOmniRiskProfileById } from '@/redux/modules/omni-risk-profiles';

// Components
import ContactPastille from '../misc/ContactPastille';
import DataTableV2 from '../data/DataTableV2/DataTableV2';
import EmptyStateCard from '@/views/components/misc/EmptyStateCard';
import ExternalLink from '@/views/components/ExternalLink';
import FormattedMessageV2 from '@/views/components/i18n/FormattedMessageV2';
import RiskProfileCard from '../common/cards/RiskProfileCard';
import { PillLabel } from '@/views/components/misc';
import { SecondaryButton } from '../buttons';
import { Tooltip } from '@/views/components/tooltips';
import { withErrorBoundary } from '@/views/components/errors';

// Enums
import {
  OmniRiskProfileStatus,
  OmniRiskProfileStatusColors,
  OmniRiskProfileSteps,
  OmniRiskProfileStepsTitlesKeys,
  OmniRiskProfilesTableKeys,
} from '@/enums/OmniRiskProfiles';

// Libraries
import classNames from 'classnames';
import moment from 'moment';
import { ColumnDef, Row } from '@tanstack/react-table';
import { Dispatch } from '@/redux';
import { RouteComponentProps, withRouter } from 'react-router';

// Types
import GenericContact from '@/types/generic-contact';
import KycQuestionnaire from '@/types/kyc-questionnaire';
import Locale from '@/types/locale';
import Nullable from '@/types/nullable';
import User from '@/types/user';
import { FormattedFullOmniRiskProfile } from '@/types/risk-profiles/omni/full-omni-risk-profile';
import { ReduxState } from '@/redux/modules/types';
import { TableClassNames } from '@/types/data-table';

// Utils
import { customSorting } from '@/utils/data-table';
import { formatLocalizedString, formatMessage } from '@/utils/i18n';
import { userHasPermissionEnabled, PERMISSIONS } from '@/utils/permissions';

type StateProps = {
  contact?: GenericContact;
  locale: Locale;
  user?: Nullable<User>;
};

type DispatchProps = {
  loadOmniRiskProfileById: (riskProfileId: FormattedFullOmniRiskProfile['_id']) => void;
  showOmniRiskProfileModal: (step: OmniRiskProfileSteps, stepTitle: OmniRiskProfileStepsTitlesKeys) => void;
};

type Props = StateProps &
  DispatchProps &
  RouteComponentProps & {
    data: FormattedFullOmniRiskProfile[];
    onCreateComparisonFromProfile?: (profile: FormattedFullOmniRiskProfile) => void;
    onCreateQuestionnaire: (questionnaire?: KycQuestionnaire) => void;
    onDelete?: (profile: FormattedFullOmniRiskProfile) => void;
    onSendQuestionnaire?: (profile: FormattedFullOmniRiskProfile) => void;
    allowsQuestionnaireActions: boolean;
  };

const OmniRiskProfilesTable = (props: Props) => {
  const {
    contact,
    data,
    loadOmniRiskProfileById,
    locale,
    location,
    onCreateComparisonFromProfile,
    onCreateQuestionnaire,
    showOmniRiskProfileModal,
    user,
    allowsQuestionnaireActions,
  } = props;

  const dispatch = useDispatch<Dispatch>();

  const searchParams = new URLSearchParams(location.search);
  const hasSendMaterialChangesReviewParam = searchParams.has('showSendMaterialChangesReview')
    ? searchParams.get('showSendMaterialChangesReview') === 'true'
    : false;
  const omniRiskProfileIdParam = searchParams.has('omniRiskProfileId') ? searchParams.get('omniRiskProfileId') : '';
  const [isMaterialChangesReviewLoaded, setIsMaterialChangesReviewLoaded] = useState(false);

  useEffect(() => {
    if (hasSendMaterialChangesReviewParam && !!omniRiskProfileIdParam && data?.length && !isMaterialChangesReviewLoaded) {
      if (!data.some(rp => rp._id === omniRiskProfileIdParam)) return setIsMaterialChangesReviewLoaded(true);
      loadOmniRiskProfileById(omniRiskProfileIdParam);
      showOmniRiskProfileModal(OmniRiskProfileSteps.SEND_REVIEW_QUESTIONNAIRE, OmniRiskProfileStepsTitlesKeys.SendQuestionnaireForReview);
      setIsMaterialChangesReviewLoaded(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data, isMaterialChangesReviewLoaded]);

  const tableClassNames: TableClassNames = {
    isBodyHoverActive: true,
  };

  const onCreateComparisonFromProfileClick = (profile: FormattedFullOmniRiskProfile) => onCreateComparisonFromProfile && onCreateComparisonFromProfile(profile);

  // EMPTY TABLE DATA
  const getEmptyDataComponent = (): JSX.Element => {
    const i18nEmptyStatePath = 'EmptyStateCard.ContactDetailsOmniRiskProfiles';
    const primaryButton = { label: `${i18nEmptyStatePath}.PrimaryButtonText`, onClick: () => onCreateQuestionnaire() };

    return (
      <EmptyStateCard
        i18nVariables={{ name: contact?.fname ?? '' }}
        messagePath={`${i18nEmptyStatePath}.Message`}
        titlePath={`${i18nEmptyStatePath}.Title`}
        {...(allowsQuestionnaireActions && { primaryButton })} // we don't want to show the button if persistence key source exists (not a Prospect or not a Client with permission)
      />
    );
  };

  // CELLS
  const getActionCell = (profile: FormattedFullOmniRiskProfile): JSX.Element => {
    const { status, omniFormUrl } = profile;
    if (!status) return <></>;

    if (status === OmniRiskProfileStatus.Review && omniFormUrl) {
      return (
        <ExternalLink className="external-link action-button" target="_self" to={`${omniFormUrl}/review`}>
          <FormattedMessageV2 toLocalize="OmniRiskProfilesTable.Columns.ReviewRiskProfile" />
        </ExternalLink>
      );
    }

    if (status === OmniRiskProfileStatus.Approved && onCreateComparisonFromProfile) {
      return (
        <SecondaryButton
          className="secondary-v2 action-button"
          onClick={() => profile && profile.status === OmniRiskProfileStatus.Approved && onCreateComparisonFromProfileClick(profile)}
        >
          <FormattedMessageV2 toLocalize="OmniRiskProfilesTable.Columns.AddToComparison" />
        </SecondaryButton>
      );
    }

    return <></>;
  };

  const getCompletedByCell = ({ completedBy, status }: FormattedFullOmniRiskProfile): JSX.Element => {
    const currentStatus = status;
    const isCompleted = currentStatus === OmniRiskProfileStatus.Approved || currentStatus === OmniRiskProfileStatus.Review;

    if (!isCompleted || !completedBy || !completedBy.id) {
      return <>–</>;
    }

    return (
      <Tooltip className={classNames('custom-tooltip-v2 contact-tooltip')} message={`${completedBy.fname} ${completedBy.lname}`} place="bottom">
        <span>
          <ContactPastille contact={completedBy} />
        </span>
      </Tooltip>
    );
  };

  const getLastUpdatedCell = (updatedAt?: Date): JSX.Element => {
    if (!updatedAt) {
      return <div className="empty-cell" />;
    }

    return <span className="cell">{moment(updatedAt).format(formatMessage('OmniRiskProfilesTable.LastUpdatedDateFormat'))}</span>;
  };

  const getMenuActions = () => [
    {
      key: 'send-questionnaire',
      className: 'primary',
      icon: 'icon-ds-mail-outline',
      isAllowed: (riskProfile?: FormattedFullOmniRiskProfile) => !!riskProfile?.status && canSendQuestionnaire(riskProfile.status, allowsQuestionnaireActions),
      onClick: (riskProfile?: FormattedFullOmniRiskProfile) => {
        if (!riskProfile) return;
        loadOmniRiskProfileById(riskProfile._id);
        const { step, stepTitle } = getSendQuestionnaireStep(riskProfile.status);
        showOmniRiskProfileModal(step, stepTitle);
      },
      value: <FormattedMessageV2 toLocalize="OmniRiskProfilesTable.SendQuestionnaire" />,
    },
    {
      key: 'complete-questionnaire',
      className: 'primary',
      icon: 'icon-ds-edit-note',
      isAllowed: (riskProfile?: FormattedFullOmniRiskProfile) =>
        !!riskProfile?.status && canCompleteQuestionnaire(riskProfile.status, allowsQuestionnaireActions),
      onClick: (riskProfile?: FormattedFullOmniRiskProfile) => {
        if (!riskProfile) return;
        loadOmniRiskProfileById(riskProfile._id);
        showOmniRiskProfileModal(OmniRiskProfileSteps.COMPLETE_QUESTIONNAIRE, OmniRiskProfileStepsTitlesKeys.CompleteQuestionnaire);
      },
      value: <FormattedMessageV2 toLocalize="OmniRiskProfilesTable.CompleteQuestionnaire" />,
    },
    {
      key: 'refresh-questionnaire',
      className: 'primary',
      'data-testid': 'omniRiskProfilesTable-iconButton-refreshQuestionnaire',
      icon: 'icon-ds-refresh',
      isAllowed: (riskProfile?: FormattedFullOmniRiskProfile) => !!riskProfile?.status && canRefreshQuestionnaire(riskProfile),
      onClick: async (riskProfile?: FormattedFullOmniRiskProfile) => {
        if (!riskProfile) return;
        loadOmniRiskProfileById(riskProfile._id);
        showOmniRiskProfileModal(OmniRiskProfileSteps.CREATE_QUESTIONNAIRE, OmniRiskProfileStepsTitlesKeys.RefreshQuestionnaire);
      },
      value: <FormattedMessageV2 toLocalize="OmniRiskProfilesTable.RefreshQuestionnaire" />,
    },
    {
      key: 'rename-profile',
      icon: 'icon-ds-rename',
      isAllowed: (riskProfile?: FormattedFullOmniRiskProfile) =>
        !!riskProfile?.name && !!formatLocalizedString(riskProfile.name) && riskProfile?.availableActions?.edit !== false,
      onClick: (riskProfile?: FormattedFullOmniRiskProfile) => {
        if (!riskProfile) return;
        loadOmniRiskProfileById(riskProfile._id);
        dispatch(setIsRenamingQuestionnaire(true));
        showOmniRiskProfileModal(OmniRiskProfileSteps.NAME_PROFILE, OmniRiskProfileStepsTitlesKeys.NameRiskProfile);
      },
      value: <FormattedMessageV2 toLocalize="OmniRiskProfilesTable.Rename" />,
    },
    {
      key: 'remove-profile',
      className: 'destructive',
      icon: 'icon-ds-delete-outline',
      isAllowed: (riskProfile?: FormattedFullOmniRiskProfile) => !!riskProfile?._id && riskProfile?.availableActions?.delete !== false,
      onClick: (riskProfile?: FormattedFullOmniRiskProfile) => {
        if (!riskProfile) return;
        loadOmniRiskProfileById(riskProfile._id);
        showOmniRiskProfileModal(OmniRiskProfileSteps.DELETE_RISK_PROFILE, OmniRiskProfileStepsTitlesKeys.DeleteRiskProfile);
      },
      value: <FormattedMessageV2 toLocalize="OmniRiskProfilesTable.Delete" />,
    },
  ];

  const getNameCell = ({ name, profile }: FormattedFullOmniRiskProfile): JSX.Element | string => {
    if (!name && !contact && !profile) {
      return <div className="empty-cell" />;
    }

    return <span className="cell name-cell">{formatLocalizedString(name)}</span>;
  };

  const getRiskProfileCell = ({ profile }: FormattedFullOmniRiskProfile): JSX.Element => {
    if (!profile) {
      return <>–</>;
    }

    return <span>{formatLocalizedString(profile.title) || '-'}</span>;
  };

  const getSendQuestionnaireStep = (status: OmniRiskProfileStatus) => {
    let step: OmniRiskProfileSteps = OmniRiskProfileSteps.SEND_INVITE_QUESTIONNAIRE;
    let stepTitle: OmniRiskProfileStepsTitlesKeys = OmniRiskProfileStepsTitlesKeys.SendQuestionnaire;

    if (status === OmniRiskProfileStatus.ClientReview || status === OmniRiskProfileStatus.ClientReviewUnsent) {
      step = OmniRiskProfileSteps.SEND_REVIEW_QUESTIONNAIRE;
      stepTitle = OmniRiskProfileStepsTitlesKeys.SendQuestionnaireForReview;
    }

    if (status === OmniRiskProfileStatus.SentRefresh) {
      step = OmniRiskProfileSteps.SEND_REFRESH_QUESTIONNAIRE;
      stepTitle = OmniRiskProfileStepsTitlesKeys.SendRefreshQuestionnaire;
    }

    return { step, stepTitle };
  };

  const getStatusCell = ({ _id, status }: FormattedFullOmniRiskProfile): JSX.Element => {
    if (!status) return <>–</>;

    const statusColor = getStatusColor(status);
    const statusKey = Object.keys(OmniRiskProfileStatus)[Object.values(OmniRiskProfileStatus).indexOf(status)];

    return (
      <PillLabel
        className={classNames('pill-label-v2', statusColor)}
        data-testid={`omniRiskProfileCard-tableCell-status-${_id}`}
        text={formatMessage(`OmniRiskProfilesTable.Status.${statusKey}`) || ''}
      />
    );
  };

  const getStatusColor = (status: OmniRiskProfileStatus) => {
    switch (status) {
      case OmniRiskProfileStatus.Approved:
        return OmniRiskProfileStatusColors.Green;
      case OmniRiskProfileStatus.Review:
        return OmniRiskProfileStatusColors.Yellow;
      case OmniRiskProfileStatus.Sent:
      case OmniRiskProfileStatus.SentRefresh:
        return OmniRiskProfileStatusColors.Blue;
      case OmniRiskProfileStatus.ClientReview:
      case OmniRiskProfileStatus.ClientReviewUnsent:
      case OmniRiskProfileStatus.Pending:
      default:
        return OmniRiskProfileStatusColors.Grey;
    }
  };

  // HEADER
  const getHeader = (i18nKey: string): string => formatMessage(`OmniRiskProfilesTable.Columns.${i18nKey}`);

  // TABLE COLUMNS
  const getTableColumns: ColumnDef<FormattedFullOmniRiskProfile>[] = useMemo(
    () => [
      {
        accessorKey: OmniRiskProfilesTableKeys.Name,
        header: getHeader('Name'),
        enableSorting: true,
        cell: ({ row }: { row: Row<FormattedFullOmniRiskProfile> }) => getNameCell(row.original),
        sortingFn: customSorting,
      },
      {
        accessorKey: OmniRiskProfilesTableKeys.Profile,
        header: getHeader('RiskProfile'),
        enableSorting: true,
        cell: ({ row }: { row: Row<FormattedFullOmniRiskProfile> }) => getRiskProfileCell(row.original),
        size: 284,
        sortingFn: customSorting,
      },
      {
        accessorKey: OmniRiskProfilesTableKeys.Status,
        header: getHeader('Status'),
        enableSorting: true,
        cell: ({ row }: { row: Row<FormattedFullOmniRiskProfile> }) => getStatusCell(row.original),
        size: 135,
        sortingFn: customSorting,
      },
      {
        accessorKey: OmniRiskProfilesTableKeys.CompletedBy,
        header: getHeader('CompletedBy'),
        enableSorting: true,
        cell: ({ row }: { row: Row<FormattedFullOmniRiskProfile> }) => getCompletedByCell(row.original),
        size: 140,
        sortingFn: customSorting,
      },
      {
        accessorKey: OmniRiskProfilesTableKeys.UpdatedAt,
        header: getHeader('LastApprovedAt'),
        cell: ({ row }: { row: Row<FormattedFullOmniRiskProfile> }) => getLastUpdatedCell(row.original.lastApprovedAt),
        size: 192,
      },
      {
        enableSorting: false,
        header: '',
        id: 'actionColumn',
        cell: ({ row }: { row: Row<FormattedFullOmniRiskProfile> }) => getActionCell(row.original),
        size: 200,
      },
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [locale],
  );

  const getExpandedContent = (row: Row<FormattedFullOmniRiskProfile>) => {
    const { _id, profile, requirements } = row.original;
    return (
      <div className="risk-profile-info">
        <RiskProfileCard key={_id} riskProfile={profile!} locale={locale} user={user} requirements={requirements} />
      </div>
    );
  };

  const canCompleteQuestionnaire = (status: OmniRiskProfileStatus, allowsQuestionnaireActions: boolean) => {
    if (!allowsQuestionnaireActions) {
      return [OmniRiskProfileStatus.SentRefresh].includes(status);
    }
    return [OmniRiskProfileStatus.Pending, OmniRiskProfileStatus.Sent, OmniRiskProfileStatus.SentRefresh].includes(status);
  };

  const canRefreshQuestionnaire = (riskProfile: FormattedFullOmniRiskProfile) => {
    const { status, profile } = riskProfile;
    return (
      !!user && userHasPermissionEnabled(user, PERMISSIONS.OMNI_REFRESH_QUESTIONNAIRE) && [OmniRiskProfileStatus.Approved].includes(status) && !!profile?.id
    );
  };

  const canSendQuestionnaire = (status: OmniRiskProfileStatus, allowsQuestionnaireActions: boolean) => {
    if (!allowsQuestionnaireActions) {
      return [OmniRiskProfileStatus.SentRefresh, OmniRiskProfileStatus.ClientReview, OmniRiskProfileStatus.ClientReviewUnsent].includes(status);
    }
    return [
      OmniRiskProfileStatus.ClientReview,
      OmniRiskProfileStatus.ClientReviewUnsent,
      OmniRiskProfileStatus.Pending,
      OmniRiskProfileStatus.Sent,
      OmniRiskProfileStatus.SentRefresh,
    ].includes(status);
  };

  return (
    <div className="risk-profiles-table">
      <DataTableV2
        columns={getTableColumns}
        data={data}
        enableSorting
        isExpandable
        initialSort={{ id: OmniRiskProfilesTableKeys.UpdatedAt, desc: true }}
        canRowExpand={({ original: { profile, requirements } }: Row<FormattedFullOmniRiskProfile>) => !!profile && !!requirements?.length}
        menuItems={getMenuActions()}
        noDataComponent={getEmptyDataComponent}
        renderExpandedContent={getExpandedContent}
        tableClassNames={tableClassNames}
      />
    </div>
  );
};

const mapStateToProps = (state: ReduxState): StateProps => {
  const {
    auth: { user },
    contacts: { current },
    intl: { locale },
  } = state;

  return {
    contact: current?.contact,
    locale,
    user,
  };
};

export default withRouter(
  withErrorBoundary(
    connect(mapStateToProps, {
      loadOmniRiskProfileById,
      showOmniRiskProfileModal,
    })(OmniRiskProfilesTable),
  ),
);
